import React, { useState } from 'react';
import { Upload, X, Check } from 'lucide-react';

// Bank Registration Modal Component
export const BankRegistrationModal = ({ isOpen, onClose, onRegister, isoCode3 }) => {
  const [bankName, setBankName] = useState('');
  const [bankLogo, setBankLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert('El archivo debe ser menor a 2MB');
        return;
      }
      
      setBankLogo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!bankName.trim()) {
      alert('Por favor ingrese el nombre del banco');
      return;
    }

    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const newBank = {
      name: bankName.trim(),
      image: logoPreview || '/default-bank-icon.svg',
      iosCode: isoCode3,
    };
    
    onRegister(newBank);
    
    // Reset form
    setBankName('');
    setBankLogo(null);
    setLogoPreview('');
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X className="w-6 h-6" />
        </button>

        <h2 className="text-2xl font-bold text-gray-800 mb-2">
          Registrar nuevo banco
        </h2>
        <p className="text-sm text-gray-600 mb-6">
          Completa la información del banco que deseas agregar
        </p>

        <div className="space-y-4">
          {/* Bank Name Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del banco <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={bankName}
              onChange={(e) => setBankName(e.target.value)}
              placeholder="Ej: Banco Internacional"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Logo del banco <span className="text-gray-400 text-xs">(Opcional)</span>
            </label>
            
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
              {logoPreview ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={logoPreview}
                      alt="Preview"
                      className="w-16 h-16 object-contain rounded border border-gray-200"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Logo cargado
                      </p>
                      <p className="text-xs text-gray-500">
                        {bankLogo?.name}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setBankLogo(null);
                      setLogoPreview('');
                    }}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center cursor-pointer">
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600 mb-1">
                    Haz clic para cargar el logo
                  </p>
                  <p className="text-xs text-gray-400">
                    PNG, JPG o SVG (máx. 2MB)
                  </p>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml"
                    onChange={handleLogoChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              Si no cargas un logo, se usará uno predeterminado
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !bankName.trim()}
            className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Registrando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Registrar banco
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};